import React, { useState, useEffect, useRef } from "react";
import {
  Zap,
  Smartphone,
  BookOpen,
  Users,
  LayoutDashboard,
  Settings,
  FileBarChart,
  PackageCheck,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { useLanguage } from "../../LanguageContext";
import { Link } from "react-router-dom";

/* ── Local reveal hook (threshold-configurable) ──────────────────────────── */
function useReveal(threshold = 0.05, rootMargin = "0px 0px -20% 0px") {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
      },
      { threshold, rootMargin }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, [threshold, rootMargin]);
  return [ref, visible];
}

export default function WrioApproach() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("small");

  /* ── Individual element refs ─────────────────────────────────── */
  const [pillRef, pillVisible]     = useReveal(0.3);
  const [headRef, headVisible]     = useReveal(0.25);
  const [tabRef, tabVisible]       = useReveal(0.2);
  const [cardRef, cardVisible]     = useReveal(0.1);

  const smallBusiness = {
    titleKey: "approach_small_title",
    featuresKeys: [
      "approach_small_f1",
      "approach_small_f2",
      "approach_small_f3",
      "approach_small_f4",
    ],
    ctaKey: "approach_small_cta",
  };

  const franchise = {
    titleKey: "approach_franchise_title",
    featuresKeys: [
      "approach_franchise_f1",
      "approach_franchise_f2",
      "approach_franchise_f3",
      "approach_franchise_f4",
    ],
    ctaKey: "approach_franchise_cta",
  };

  const smallBusinessFeatures = [
    { icon: Zap, textKey: "approach_sb_feat_qsetup", color: "text-amber-500", bg: "bg-amber-50" },
    { icon: Smartphone, textKey: "approach_sb_feat_orderphone", color: "text-teal-600", bg: "bg-teal-50" },
    { icon: BookOpen, textKey: "approach_sb_feat_menu", color: "text-violet-500", bg: "bg-violet-50" },
    { icon: Users, textKey: "approach_sb_feat_customers", color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  const franchiseFeatures = [
    { icon: LayoutDashboard, textKey: "approach_fr_feat_dashboard", color: "text-teal-600", bg: "bg-teal-50" },
    { icon: Settings, textKey: "approach_fr_feat_headcontrols", color: "text-slate-600", bg: "bg-slate-50" },
    { icon: FileBarChart, textKey: "approach_fr_feat_reports", color: "text-violet-500", bg: "bg-violet-50" },
    { icon: PackageCheck, textKey: "approach_fr_feat_stock", color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  const isSmall = activeTab === "small";
  const activeConfig = isSmall ? smallBusiness : franchise;
  const activeFeatures = isSmall ? smallBusinessFeatures : franchiseFeatures;
  const activeFeaturesTitle = isSmall
    ? t("approach_sb_features_title")
    : t("approach_fr_features_title");

  return (
    <section className="relative bg-[#fafafa] py-12 px-4 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
        <div className="absolute -right-48 top-1/4 w-96 h-96 bg-teal-50 rounded-full blur-3xl opacity-70" />
        <div className="absolute -left-48 bottom-1/4 w-96 h-96 bg-emerald-50 rounded-full blur-3xl opacity-70" />
      </div>

      <div className="relative max-w-6xl mx-auto">

        {/* ── Section label pill ────────────────────────────────── */}
        <div
          ref={pillRef}
          className={`text-center mb-5 reveal-fade reveal-slow ${pillVisible ? "revealed" : ""}`}
        >
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-teal-700 bg-teal-50 border border-teal-100 px-4 py-2 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block" />
            Built for Every Business
          </span>
        </div>

        {/* ── Section heading ──────────────────────────────────── */}
        <div
          ref={headRef}
          className={`text-center mb-5 reveal-up reveal-slow ${headVisible ? "revealed" : ""}`}
        >
          <h2
            className="text-4xl lg:text-5xl font-extrabold text-gray-950 tracking-tight leading-tight mb-4"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            One Platform,{" "}
            <span className="bg-gradient-to-r from-teal-600 to-emerald-500 bg-clip-text text-transparent">
              Two Powerful Modes
            </span>
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto font-normal">
            Whether you're launching your first store or managing a chain of outlets — Wrio adapts to exactly what you need.
          </p>
        </div>

        {/* ── Tab switcher ─────────────────────────────────────── */}
        <div
          ref={tabRef}
          className={`flex justify-center mb-5 reveal-scale reveal-delay-1 ${tabVisible ? "revealed" : ""}`}
        >
          <div className="inline-flex bg-white border border-gray-200 rounded-xl p-1 shadow-sm gap-1">
            <button
              onClick={() => setActiveTab("small")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === "small"
                  ? "bg-gradient-to-r from-teal-600 to-emerald-500 text-white shadow-md shadow-teal-200/50"
                  : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
              }`}
            >
              <span className="text-base">🏪</span>
              {t("approach_small_title")}
            </button>
            <button
              onClick={() => setActiveTab("franchise")}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === "franchise"
                  ? "bg-gradient-to-r from-teal-600 to-emerald-500 text-white shadow-md shadow-teal-200/50"
                  : "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
              }`}
            >
              <span className="text-base">🏢</span>
              {t("approach_franchise_title")}
            </button>
          </div>
        </div>

        {/* ── Main content card ─────────────────────────────────── */}
        <div
          ref={cardRef}
          className={`reveal-up reveal-slow reveal-delay-2 ${cardVisible ? "revealed" : ""}`}
        >
          <div className="bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/40 overflow-hidden">
            <div className="grid lg:grid-cols-2">

              {/* Left — pitch */}
              <div className="p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-gray-100">
                <div className="mb-8">
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl mb-5 ${
                    isSmall ? "bg-teal-50" : "bg-violet-50"
                  }`}>
                    <span className="text-2xl">{isSmall ? "🏪" : "🏢"}</span>
                  </div>
                  <h3
                    className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 leading-tight"
                    style={{ fontFamily: "'Sora', sans-serif" }}
                  >
                    {t(activeConfig.titleKey)}
                  </h3>
                  <p className="text-gray-500 text-base leading-relaxed">
                    {isSmall
                      ? "Launch your online store in minutes with zero technical skills. Start taking orders today."
                      : "Manage all your outlets from a single powerful dashboard. Scale confidently across cities."}
                  </p>
                </div>

                {/* Feature checklist — staggered */}
                <ul className="space-y-3 mb-10">
                  {activeConfig.featuresKeys.map((key, idx) => (
                    <li
                      key={key}
                      className={`flex items-center gap-3 reveal-left reveal-delay-${idx + 3} ${cardVisible ? "revealed" : ""}`}
                    >
                      <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700 font-medium text-sm">{t(key)}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact#contact-form"
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-emerald-500 text-white rounded-xl font-semibold text-sm shadow-md shadow-teal-200/50 hover:shadow-lg hover:shadow-teal-200/70 hover:-translate-y-0.5 transition-all duration-200 no-underline"
                >
                  {t(activeConfig.ctaKey)}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>

              {/* Right — feature icon cards */}
              <div className="p-8 lg:p-12 bg-gradient-to-br from-gray-50/50 to-white">
                <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-400 mb-6">
                  {activeFeaturesTitle}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {activeFeatures.map((feature, idx) => {
                    const Icon = feature.icon;
                    return (
                      <div
                        key={idx}
                        className={`group bg-white rounded-2xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-teal-100 hover:-translate-y-0.5 transition-all duration-200 cursor-default reveal-scale reveal-delay-${idx + 1} ${cardVisible ? "revealed" : ""}`}
                      >
                        <div className={`w-10 h-10 ${feature.bg} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-200`}>
                          <Icon className={`w-5 h-5 ${feature.color}`} />
                        </div>
                        <p className="text-sm font-semibold text-gray-800 leading-snug">
                          {t(feature.textKey)}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Mini stat badges — staggered */}
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {(isSmall
                    ? ["Launch in 5 min", "No tech needed", "Free to start"]
                    : ["Multi-outlet", "Real-time sync", "Head office control"]
                  ).map((label, i) => (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-full reveal-fade reveal-delay-${i + 5} ${cardVisible ? "revealed" : ""}`}
                    >
                      <ChevronRight className="w-3 h-3" />
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
